import { CakeIcon, CalendarXIcon, ClipboardTextIcon, FlagIcon, ToothIcon, UserCircleIcon } from '@phosphor-icons/react'
import { hygieneBandSearchTerms, hygieneDayWindowBand } from 'app/components/action-center/hygieneBands'
import { leadDayWindowBand } from 'lib/leadBands'
import {
  ACTION_QUEUE_BY_ID,
  ACTIVE_LEAD_DAYS,
  type ActionQueueDef,
  activeLeadsSearchTerms,
  type QueueSearchOpts,
} from 'app/components/action-center/queueRegistry'
import { TREATMENT_BANDS, TREATMENT_POPULATION } from 'app/components/action-center/treatmentBands'
import type { ListView, NavItemsInput, SystemListBand, SystemListDef } from 'app/types/navigation'
import type { PmsGate } from 'app/types/pms'
import type { NavItemType } from 'types/Sidebar'

const LIST_VIEWS: ReadonlyArray<ListView> = [`focus`, `board`, `table`]
export const ALL_BANDS = `all`

function queue(id: ActionQueueDef[`id`]): ActionQueueDef {
  const def = ACTION_QUEUE_BY_ID.get(id)
  if (!def) throw new Error(`Unknown queue for system list: ${id}`)
  return def
}

function band(id: string, queueId: ActionQueueDef[`id`], label?: string): SystemListBand {
  const bandQueue = queue(queueId)
  return { id, label: label ?? bandQueue.shortName ?? bandQueue.name, queue: bandQueue }
}

export const SYSTEM_LISTS: ReadonlyArray<SystemListDef> = [
  {
    slug: `active-leads`,
    name: `Active Leads`,
    recordType: `contacts`,
    listKind: `leads`,
    statusField: `leadStatus`,
    exitStageNames: [`Scheduled`, `Closed Lost`],
    views: LIST_VIEWS,
    queue: queue(`active-leads`),
    bands: [band(`last-${ACTIVE_LEAD_DAYS}-days`, `active-leads`, `Last ${ACTIVE_LEAD_DAYS} days`)],
    defaultBand: `last-${ACTIVE_LEAD_DAYS}-days`,
    opensOnDefaultBand: true,
    bandMenuLabel: `Became a lead`,
    bandRuleName: `Became a lead`,
    bandDayWindows: [
      { kind: `last`, label: `In the last X days`, before: `In the last`, after: `days`, defaultDays: 7 },
    ],
    dayWindowBand: leadDayWindowBand,
    allSearchTerms: (opts) => activeLeadsSearchTerms(undefined, opts),
    fixedRules: () => [
      { label: `Lead status is Needs Action or In Progress`, icon: FlagIcon },
      { label: `Not marked Non-patient`, icon: UserCircleIcon },
    ],
    allBandBlurb: `Every open lead, however long ago they came in.`,
  },
  {
    slug: `hygiene-recall`,
    name: `Hygiene Recall`,
    recordType: `contacts`,
    listKind: `hygiene`,
    statusField: `hygieneStatus`,
    setupTemplate: [
      { name: `Reached out`, color: `pipelines-blue` },
      { name: `Responded`, color: `pipelines-amber` },
    ],
    views: LIST_VIEWS,
    queue: queue(`due-for-hygiene`),
    // Band ids are the queue ids the hygiene board already reads from `?band=`.
    bands: [
      band(`hygiene-coming-due`, `hygiene-coming-due`),
      band(`due-for-hygiene`, `due-for-hygiene`),
      band(`hygiene-long-past-due`, `hygiene-long-past-due`),
    ],
    defaultBand: `hygiene-coming-due`,
    bandMenuLabel: `Cleaning due`,
    bandRuleName: `Cleaning due`,
    bandDayWindows: [
      { kind: `due`, label: `Due within X days`, before: `Due within`, after: `days`, defaultDays: 14 },
      { kind: `overdue`, label: `Over X days overdue`, before: `Over`, after: `days overdue`, defaultDays: 60 },
    ],
    dayWindowBand: hygieneDayWindowBand,
    allSearchTerms: (opts) => hygieneBandSearchTerms(undefined, opts),
    fixedRules: ({ includeBooked }) => [
      { label: `Patient status is not Non-patient, Inactive or Archived`, icon: UserCircleIcon },
      { label: `Age 18 or older`, icon: CakeIcon },
      { label: `Not a perio patient`, icon: ToothIcon },
      ...(includeBooked ? [] : [{ label: `No upcoming appointment`, icon: CalendarXIcon }]),
    ],
    allBandBlurb: `Everyone due a cleaning, whenever it fell due.`,
  },
  {
    slug: `unscheduled-treatment`,
    name: `Unscheduled Tx`,
    recordType: `treatmentPlans`,
    listKind: `treatment`,
    statusField: `treatmentStatus`,
    exitStageNames: [`Lost`],
    bookedStage: `Scheduled`,
    setupTemplate: [
      { name: `Reached out`, color: `pipelines-blue` },
      { name: `Considering`, color: `pipelines-amber` },
      { name: `Ready to schedule`, color: `pipelines-green` },
    ],
    views: LIST_VIEWS,
    queue: queue(`treatment-fresh`),
    bands: TREATMENT_BANDS.map((treatmentBand) =>
      band(treatmentBand.id, `treatment-${treatmentBand.id}` as ActionQueueDef[`id`], treatmentBand.shortName),
    ),
    defaultBand: `fresh`,
    bandMenuLabel: `Treatment presented`,
    bandNoun: `Presented`,
    bandRuleName: `Presented`,
    allSearchTerms: () => [TREATMENT_POPULATION],
    fixedRules: () => [{ label: `Has unscheduled treatment`, icon: ClipboardTextIcon }],
    allBandBlurb: `Every case with treatment outstanding, however long ago it was planned.`,
  },
]

export const SYSTEM_LIST_BY_SLUG: ReadonlyMap<string, SystemListDef> = new Map(
  SYSTEM_LISTS.map((def) => [def.slug, def]),
)

export const TREATMENT_LIST: SystemListDef = SYSTEM_LIST_BY_SLUG.get(`unscheduled-treatment`)!

export function systemListId(def: Pick<SystemListDef, `listKind`>, workspaceId: string): string {
  return `system:${def.listKind}:${workspaceId}`
}

type QueueIdEntry = readonly [string, { def: SystemListDef; bandId?: string }]

/** Every queue id, mapped to the family it belongs to and the band it is inside it. A list that opens on its
 * default band everywhere leaves that band out, so its links stay bare. */
export const SYSTEM_LIST_BY_QUEUE_ID: ReadonlyMap<string, { def: SystemListDef; bandId?: string }> = new Map(
  SYSTEM_LISTS.flatMap((def): QueueIdEntry[] =>
    def.bands
      ? def.bands.map(
          (listBand): QueueIdEntry => [
            listBand.queue.id,
            { def, bandId: def.opensOnDefaultBand && listBand.id === def.defaultBand ? undefined : listBand.id },
          ],
        )
      : [[def.queue.id, { def, bandId: undefined }]],
  ),
)

/** Contacts presets V3 keeps off Lists: three a system list replaces, plus Scheduled Leads, which is an
 * outcome to report on rather than a queue to work. V2 still renders them all. */
export const RETIRED_CONTACT_PRESETS: ReadonlySet<string> = new Set([
  `active-leads`,
  `due-for-hygiene`,
  `due-in-30-days`,
  `scheduled-leads`,
  `closed-lost-leads`,
])

/** Where a system pipeline's sidebar row opens once boards live on list pages. */
export const SYSTEM_PIPELINE_LIST = Object.freeze({
  'system:leads': `active-leads`,
  'system:hygiene': `hygiene-recall`,
  'system:treatment': `unscheduled-treatment`,
} as const)

export function legacyPipelineIdOf(def: Pick<SystemListDef, `slug`>): string | undefined {
  return Object.entries(SYSTEM_PIPELINE_LIST).find(([, slug]) => slug === def.slug)?.[0]
}

export function listPath(slug: string, view?: ListView, bandId?: string): string {
  const params = new URLSearchParams()
  if (view) params.set(`view`, view)
  if (bandId) params.set(`band`, bandId)
  const query = params.toString()
  return query ? `/lists/${slug}?${query}` : `/lists/${slug}`
}

/**
 * The band a `?band=` value names for a view. Board and Table accept `all` (undefined)
 * and widen on an unknown value, like the boards do. One at a time works one band and
 * falls back to the default instead.
 */
export function resolveListBand(def: SystemListDef, param: string | null, view: ListView): SystemListBand | undefined {
  if (!def.bands) return undefined
  const named = def.bands.find((listBand) => listBand.id === param)
  const fallback = def.bands.find((listBand) => listBand.id === def.defaultBand) ?? def.bands[0]
  if (named) return named
  if (view === `focus`) return fallback
  const dayWindow = param === null ? undefined : def.dayWindowBand?.(param)
  if (dayWindow) return dayWindow
  // The board opens on its default window because a board of every band is too wide to
  // work. A table can sort and filter its way to the recent plans, so it opens on all of
  // them rather than on a window that is often empty.
  if (param === null && (view === `board` || def.opensOnDefaultBand)) return fallback
  return undefined
}

export function familySearchTerms(
  def: SystemListDef,
  listBand: SystemListBand | undefined,
  opts: QueueSearchOpts,
): string[] {
  if (listBand) return listBand.queue.searchTerms(opts)
  if (def.allSearchTerms) return def.allSearchTerms(opts)
  return def.queue.searchTerms(opts)
}

// An exit status hides its members from the list's board and count; the record keeps its other facts.
// `.keyword`, because the status field is analysed and only its keyword matches a name exactly.
export function hiddenStatusTerms(def: SystemListDef): string[] {
  if (!def.statusField || def.statusField === `leadStatus`) return []
  return (def.exitStageNames ?? []).map((name) => `!${def.statusField}.keyword:"${name}"`)
}

// What is still to book: the hidden terms, minus the contacts a booking already moved to the booked stage.
export function toBookStatusTerms(def: SystemListDef): string[] {
  const booked = def.statusField && def.bookedStage ? [`!${def.statusField}.keyword:"${def.bookedStage}"`] : []
  return [...hiddenStatusTerms(def), ...booked]
}

/**
 * Whether a system list is offered at all (`if`) and whether it is shown locked. The sidebar
 * row and a contact's rule rows share these gates, so a row never links to a locked list.
 */
export function systemListGate(
  def: SystemListDef,
  { hasWorkspace, treatmentGate }: { hasWorkspace: boolean; treatmentGate: PmsGate },
): Pick<NavItemType, `if` | `isLocked`> {
  const hygiene = def.queue.bundle === `hygiene`
  const treatment = def.queue.bundle === `treatment`
  const patient = def.queue.family === `patient`
  const offered = hygiene ? hasWorkspace : patient ? hasWorkspace : true
  return {
    if: offered && !(treatment && treatmentGate === `hidden`),
    isLocked: treatment && treatmentGate === `padlock`,
  }
}

export function systemListItem(def: SystemListDef, input: NavItemsInput): NavItemType {
  const { location, counts, workspace, treatmentGate } = input
  return {
    name: def.name,
    href: listPath(def.slug),
    active: location.pathname === listPath(def.slug),
    count: counts.contacts?.[def.slug],
    product: `CRM`,
    feature: `action-center`,
    // Same gates the Action Center applies to its cards (useActionCenter.visibleQueues).
    ...systemListGate(def, { hasWorkspace: !!workspace, treatmentGate }),
  }
}

export function resolveListView(
  def: SystemListDef,
  urlView: string | null,
  savedView: string | undefined,
  legacyView: string | undefined,
): ListView {
  const offered = (view: string | null | undefined): view is ListView => def.views.includes(view as ListView)
  if (offered(urlView)) return urlView
  if (offered(savedView)) return savedView
  if (offered(legacyView)) return legacyView
  if (offered(def.queue.defaultView)) return def.queue.defaultView
  return def.views[0] ?? `focus`
}
